/**
 * N-column grid — slots authored under {@code col1..col4}.
 *
 * <p>Each column prop accepts either a pre-rendered {@code ReactNode}
 * (Puck editor path, where slot functions are called before this
 * component runs) or a {@code ReactNode[]} already resolved by
 * {@code RenderOneBlock}'s slot pre-render step (portal path).
 *
 * <p>{@code stackOnMobile}: collapses the 2-column grid to a single
 * column at ≤ 1000 px when true. 3- and 4-column layouts (1-1-1,
 * 1-1-1-1, 1-2-1) always collapse to a single full-width column at
 * ≤ 1023 px regardless of this prop, and 2-col layouts collapse at
 * ≤ 640 px regardless of this prop — a multi-col layout at tablet/
 * mobile widths is never usable. The desktop layout ratio (1-2, 2-1,
 * etc.) is always applied via the inline {@code gridTemplateColumns}
 * style so {@code layout} reflects correctly on all viewports above
 * these breakpoints.
 */
import type { ReactNode, CSSProperties } from 'react';
import { BlockRoot } from '../_shared/BlockRoot';
import { CollapseIfEmpty } from './CollapseIfEmpty';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';

// Only top/bottom are honoured — left/right are owned by CENTER_MARGIN's
// auto-centering below. The Puck "Margin" panel defaults every side to "0"
// independently the moment it's touched, so an authored left/right of "0"
// is indistinguishable from "never configured" and would silently cancel
// centering (see seed-data/blocks/Columns.json, every instance has all
// four sides populated). Horizontal centering isn't a per-instance
// concern, so left/right from marginOuter are ignored entirely.
function resolveMarginStyle(box: PaddingBox | undefined): CSSProperties {
  if (!box) return {};
  const SPACE_FALLBACKS: Record<string, string> = {
    '0': '0px',
    '1': '4px',
    '2': '8px',
    '3': '12px',
    '4': '16px',
    '5': '20px',
    '6': '24px',
    '7': '28px',
    '8': '32px',
    '9': '36px',
    '10': '40px',
    '12': '48px',
    '14': '56px',
    '16': '64px',
    '20': '80px',
    '24': '96px',
  };
  const toVar = (k: string | undefined) => {
    if (k == null) return undefined;
    const fb = SPACE_FALLBACKS[k];
    if (!fb) return undefined;
    return k === '0' ? '0px' : `var(--space-${k}, ${fb})`;
  };
  const out: CSSProperties = {};
  const t = toVar(box.top),
    b = toVar(box.bottom);
  if (t != null) out.marginTop = t;
  if (b != null) out.marginBottom = b;
  return out;
}

// Expressed as longhand (not the `margin` shorthand) so it can be spread
// into the same style object as `outerMar` (also longhand, from
// resolveMarginStyle) without mixing shorthand/longhand for the same
// property — React's inline-style reconciliation warns this combination
// is unsafe across rerenders (e.g. a client-side nav): the shorthand can
// fail to reapply once diffed against an unchanged previous value, even
// though a full page load (no prevStyles to diff against) always works.
const CENTER_MARGIN: CSSProperties = {
  marginTop: '0px',
  marginRight: 'auto',
  marginBottom: '0px',
  marginLeft: 'auto',
};

const RESPONSIVE_CSS = `
  @media(max-width:1023px){
    [data-cols="3"],
    [data-cols="4"] { display: block!important; }
    [data-cols="3"] > * + *,
    [data-cols="4"] > * + * { margin-top: var(--cd-columns-gap, 0)!important; }
    [data-cols="3"] .cd-columns-cell,
    [data-cols="4"] .cd-columns-cell { height: auto!important; }
    [data-sticky-cell] { position: static!important; }
  }
  @media(max-width:1000px){
    /* display:block drops the grid's "gap" — restore it as margin-top on
       every stacked cell after the first, via the --cd-columns-gap custom
       property set inline on the grid container. */
    [data-cols="2"][data-stack-on-mobile="true"] { display: block!important; }
    [data-cols="2"][data-stack-on-mobile="true"] > * + * { margin-top: var(--cd-columns-gap, 0)!important; }
    /* stretchItems' .cd-columns-cell{height:100%} needs a definite-height
       grid row to resolve against. Once the grid becomes display:block its
       height goes auto/indefinite, so height:100% resolves to nothing
       (spec) or collapses to 0 in practice — starving anything inside that
       measures its own box (e.g. a recharts ResponsiveContainer, which
       never mounts its SVG below a non-zero measured height). Drop the
       stretch once stacked; there's only one column per row anyway. */
    [data-cols="2"][data-stack-on-mobile="true"] .cd-columns-cell { height: auto!important; }
    [data-sticky-cell] { position: static!important; }
  }
  @media(max-width:640px){
    [data-cols="2"] { display: block!important; }
    [data-cols="2"] > * + * { margin-top: var(--cd-columns-gap, 0)!important; }
    [data-cols="2"] .cd-columns-cell { height: auto!important; }
    [data-sticky-cell] { position: static!important; }
  }
`;

// Any block dropped into a column cell may internally cap its own width
// via `--sb-max-width-1200` (read by the shared `sectionInner` style and by
// SectionShell — see portal.css). Inside a column the cell itself IS the
// layout context — nothing nested inside should try to further center/cap
// itself against the page's content width. Overriding the custom property
// here (rather than targeting a class) reaches every consumer of the var,
// including ones that spread `sectionInner` directly without going through
// SectionShell (e.g. ButtonGroup) — no per-component change needed. Always
// applied (not gated by stretchItems), so it covers every column layout.
const COL_CELL_CSS = `
  .cd-col-cell {
    --sb-max-width-1200: 100%;
  }
`;

// Opt-in via `stretchItems`: makes every cell — and its single rendered
// child (typically a themed block's own card) — fill the grid row's full
// height, so cards with uneven content (e.g. an empty-state column next to
// a populated table) end up the same height instead of looking staggered.
const STRETCH_CSS = `
  .cd-columns-cell { display: flex; flex-direction: column; height: 100%; }
  .cd-columns-cell > * { flex: 1 1 auto; min-height: 0; }
`;

// "main-aside" is a special 3-cell responsive layout (not a simple column
// template): a wide MAIN cell plus a split aside — an aside-TOP cell and an
// aside-BOTTOM cell. On desktop the aside sits in a fixed-width right column
// (top above bottom); on mobile the three cells reflow to a single column in
// the order aside-top → main → aside-bottom, so e.g. a course-detail enroll
// card (aside-top) floats above the main content while the rest of the
// sidebar (aside-bottom) stays below it. Maps col1→main, col2→aside-top,
// col3→aside-bottom. Driven by grid-template-areas in MAIN_ASIDE_CSS.
const MAIN_ASIDE_CSS = `
  .cd-main-aside {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: "aside-top" "main" "aside-bottom";
    align-items: start;
  }
  @media(min-width:1024px){
    .cd-main-aside {
      grid-template-columns: minmax(0,1fr) var(--app-course-aside-width, 340px);
      grid-template-areas: "main aside-top" "main aside-bottom";
      /* Row 1 = the enroll card's own height; row 2 soaks up the rest. Without
         this the rows default to auto/auto and MAIN (which spans both, and is
         much taller) distributes its height evenly across both rows — that
         inflates row 1 and pushes the aside-bottom (trainers) card down toward
         the middle/bottom of the right column. Pinning row 1 to auto keeps the
         two aside cards stacked at the top, directly under each other. */
      grid-template-rows: auto 1fr;
    }
  }
`;

const columnsLayoutMap: Record<string, string> = {
  '1-1': 'minmax(0,1fr) minmax(0,1fr)',
  '2-1': 'minmax(0,2fr) minmax(0,1fr)',
  '1-2': 'minmax(0,1fr) minmax(0,2fr)',
  '3-1': 'minmax(0,3fr) minmax(0,1fr)',
  '1-3': 'minmax(0,1fr) minmax(0,3fr)',
  '4-1': 'minmax(0,4fr) minmax(0,1fr)',
  '1-4': 'minmax(0,1fr) minmax(0,4fr)',
  '1-1-1': 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
  '1-2-1': 'minmax(0,1fr) minmax(0,2fr) minmax(0,1fr)',
  '1-1-1-1': 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
};

const columnsCountMap: Record<string, number> = {
  '1-1': 2,
  '2-1': 2,
  '1-2': 2,
  '3-1': 2,
  '1-3': 2,
  '4-1': 2,
  '1-4': 2,
  '1-1-1': 3,
  '1-2-1': 3,
  '1-1-1-1': 4,
};

const columnsGapMap: Record<string, string> = {
  none: '0',
  xs: 'var(--space-2, 8px)',
  sm: 'var(--space-3, 12px)',
  md: 'var(--space-6, 24px)',
  lg: 'var(--space-10, 40px)',
  xl: 'var(--space-16, 64px)',
  xxl: 'var(--space-24, 96px)',
};

/** Vertical gap between blocks stacked INSIDE a column. {@code none}
 *  keeps the legacy plain-block flow (each child brings its own
 *  spacing); the others turn the column into a flex stack. */
const innerGapMap: Record<string, string> = {
  none: '0',
  sm: 'var(--space-3, 12px)',
  md: 'var(--space-6, 24px)',
  lg: 'var(--space-10, 40px)',
};

export interface ColumnsProps {
  layout?: keyof typeof columnsLayoutMap;
  gap?: keyof typeof columnsGapMap;
  innerGap?: keyof typeof innerGapMap;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  marginOuter?: PaddingBox;
  /**
   * Row width. {@code 'theme'} (default) caps the row at the theme's content
   * max-width token ({@code --sb-max-width-1200}/{@code --app-max-width}),
   * matching page content. {@code 'full'} lets the row span edge-to-edge.
   * Any other value is passed through as a raw CSS max-width (legacy
   * authored content may still carry a literal token string here).
   */
  maxWidth?: 'theme' | 'full' | string;
  stackOnMobile?: boolean;
  /**
   * When true, columns whose content renders to nothing (null/empty client
   * components) are hidden and the remaining columns expand to fill the space.
   * Uses `auto-fit` grid + a client-side empty-detection wrapper per cell.
   */
  collapseEmpty?: boolean;
  /**
   * Which column (1-indexed) should be made sticky. All blocks inside that
   * column scroll with the viewport as a single unit.
   */
  stickyCol?: number;
  /** Top offset in px when stickyCol is set (e.g. header height). Default 0. */
  stickyTop?: number;
  /**
   * When true, stretches each column's content to fill the row's full
   * height, so cards with different amounts of content (e.g. an empty
   * approvals list next to a populated one) line up at the bottom
   * instead of ending at different heights. Default false (legacy
   * top-aligned/auto-height behaviour).
   */
  stretchItems?: boolean;
  /** Pre-rendered ReactNode — resolved by RenderOneBlock before this runs. */
  col1?: ReactNode;
  col2?: ReactNode;
  col3?: ReactNode;
  col4?: ReactNode;
}

export function Columns({
  layout = '1-1',
  gap = 'md',
  innerGap = 'none',
  paddingOuter,
  paddingInner,
  marginOuter,
  maxWidth = 'theme',
  stackOnMobile = true,
  collapseEmpty = false,
  stickyCol,
  stickyTop = 0,
  stretchItems = false,
  col1,
  col2,
  col3,
  col4,
}: ColumnsProps) {
  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  const outerMar = resolveMarginStyle(marginOuter);
  const resolvedMaxWidth =
    maxWidth === 'theme'
      ? 'var(--sb-max-width-1200, var(--app-max-width, 1200px))'
      : maxWidth === 'full'
        ? 'none'
        : maxWidth;

  if (layout === 'main-aside') {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: MAIN_ASIDE_CSS }} />
        <style dangerouslySetInnerHTML={{ __html: COL_CELL_CSS }} />
        <BlockRoot
          as="div"
          className="sb-app-max-width"
          style={{
            maxWidth: resolvedMaxWidth,
            width: '100%',
            ...CENTER_MARGIN,
            ...outerPad,
            ...outerMar,
          }}
        >
          <div
            className="cd-main-aside"
            style={{ gap: columnsGapMap[gap] ?? columnsGapMap['md'], ...innerPad }}
          >
            <div className="cd-col-cell" style={{ gridArea: 'main' }}>
              {col1}
            </div>
            <div className="cd-col-cell" style={{ gridArea: 'aside-top' }}>
              {col2}
            </div>
            <div className="cd-col-cell" style={{ gridArea: 'aside-bottom' }}>
              {col3}
            </div>
          </div>
        </BlockRoot>
      </>
    );
  }

  const activeCount = columnsCountMap[layout] ?? 2;
  const cols = [col1, col2, col3, col4].slice(0, activeCount);

  const innerColumnStyle =
    innerGap === 'none'
      ? undefined
      : ({ display: 'flex', flexDirection: 'column', gap: innerGapMap[innerGap] } as const);

  const stickyColStyle = (idx: number): CSSProperties | undefined =>
    stickyCol && Number(stickyCol) === idx + 1
      ? { position: 'sticky', top: Number(stickyTop) || 0, alignSelf: 'start' }
      : undefined;

  // When collapseEmpty is on, use auto-fit so hidden cells don't leave a gap.
  const gridTemplateColumns = collapseEmpty
    ? `repeat(auto-fit, minmax(0, 1fr))`
    : (columnsLayoutMap[layout] ?? columnsLayoutMap['1-1']);

  const CellWrapper = collapseEmpty ? CollapseIfEmpty : 'div';

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <style dangerouslySetInnerHTML={{ __html: COL_CELL_CSS }} />
      {stretchItems && <style dangerouslySetInnerHTML={{ __html: STRETCH_CSS }} />}
      <BlockRoot
        as="div"
        className="sb-app-max-width"
        style={{
          maxWidth: resolvedMaxWidth,
          width: '100%',
          ...CENTER_MARGIN,
          ...outerPad,
          ...outerMar,
        }}
      >
        <div
          style={
            {
              display: 'grid',
              gridTemplateColumns,
              gap: columnsGapMap[gap] ?? columnsGapMap['md'],
              ...innerPad,
              '--cd-columns-gap': columnsGapMap[gap] ?? columnsGapMap['md'],
            } as CSSProperties
          }
          data-cols={activeCount}
          data-stack-on-mobile={stackOnMobile ? 'true' : undefined}
        >
          {cols.map((c, i) => {
            const isSticky = Boolean(stickyCol) && Number(stickyCol) === i + 1;
            return (
              <CellWrapper
                key={i}
                className={[
                  'cd-col-cell',
                  stretchItems && !isSticky ? 'cd-columns-cell' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ ...innerColumnStyle, ...stickyColStyle(i) }}
                {...(isSticky ? { 'data-sticky-cell': 'true' } : {})}
              >
                {c}
              </CellWrapper>
            );
          })}
        </div>
      </BlockRoot>
    </>
  );
}
